import { CustomerSearchDataFragment } from "@bonliva-crm/graphql";
import { Loader } from "@bonliva-crm/ui";
import { CustomerCard } from "./customer-card";
import { Box, Typography, makeStyles } from "@material-ui/core";
import locale from "@bonliva-crm/locale";

type Props<
  IsMulti extends boolean,
  T = IsMulti extends true
    ? CustomerSearchDataFragment[]
    : CustomerSearchDataFragment | null
> = {
  customers: CustomerSearchDataFragment[];
  selectedCustomers: T;
  setSelectedCustomers: (customers: T) => void;
  isLoading?: boolean;
  isEmptySearch?: boolean;
  isMulti: IsMulti;
  instantSelection?: IsMulti extends true ? never : boolean;
};

export const CustomerCardList = <
  IsMulti extends boolean,
  T = IsMulti extends true
    ? CustomerSearchDataFragment[]
    : CustomerSearchDataFragment | null
>({
  customers,
  selectedCustomers,
  setSelectedCustomers,
  isLoading,
  isEmptySearch,
  isMulti,
  instantSelection
}: Props<IsMulti, T>) => {
  const selectedCustomersArray = (
    Array.isArray(selectedCustomers)
      ? selectedCustomers
      : selectedCustomers
      ? [selectedCustomers]
      : []
  ) as CustomerSearchDataFragment[];

  const classes = useStyles();
  if (isLoading && selectedCustomersArray.length === 0) {
    return <Loader />;
  }

  if (isEmptySearch && selectedCustomersArray.length === 0) {
    return (
      <Typography variant="body2" className={classes.text}>
        {locale.customerEmptySearchText}
      </Typography>
    );
  }

  if (customers.length === 0 && selectedCustomersArray.length === 0) {
    return (
      <Typography variant="body2" className={classes.text}>
        {locale.NoSearchResults}
      </Typography>
    );
  }

  const customersNotInSearchResult = isLoading
    ? selectedCustomersArray
    : selectedCustomersArray.filter((sc) => !customers.some((c) => c._id === sc._id));

  return (
    <Box className={classes.root}>
      <Typography variant="body2" className={classes.text}>
        {locale.Results}
      </Typography>
      <div className={classes.scrollContainer}>
        <div className={classes.customerContainer}>
          {customersNotInSearchResult.map((customer) => (
            <CustomerCard
              customer={customer}
              key={customer._id}
              selected={selectedCustomersArray.some((c) => c._id === customer._id)}
              setSelected={(id) => {
                if (selectedCustomersArray.some((c) => c._id === id)) {
                  const filtered = selectedCustomersArray.filter((c) => c._id !== id);
                  const singleCustomer = filtered.length === 1 ? filtered[0] : null;
                  const multiCustomer = filtered;
                  setSelectedCustomers((isMulti ? multiCustomer : singleCustomer) as T);
                } else {
                  setSelectedCustomers(
                    (isMulti ? [...selectedCustomersArray, customer] : customer) as T
                  );
                }
              }}
              showRadioButton={!instantSelection}
            />
          ))}
          {isLoading ? (
            <Loader />
          ) : (
            customers.map((customer) => (
              <CustomerCard
                customer={customer}
                key={customer._id}
                selected={selectedCustomersArray.some((c) => c._id === customer._id)}
                setSelected={(id) => {
                  if (selectedCustomersArray.some((c) => c._id === id)) {
                    const filtered = selectedCustomersArray.filter((c) => c._id !== id);
                    const singleCustomer = filtered.length === 1 ? filtered[0] : null;
                    const multiCustomer = filtered;
                    setSelectedCustomers((isMulti ? multiCustomer : singleCustomer) as T);
                  } else {
                    setSelectedCustomers(
                      (isMulti ? [...selectedCustomersArray, customer] : customer) as T
                    );
                  }
                }}
                showRadioButton={!instantSelection}
              />
            ))
          )}
        </div>
      </div>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  },
  scrollContainer: {
    maxHeight: "50vh",
    overflow: "auto"
  },
  customerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.0rem"
  },
  text: {
    marginTop: "1rem"
  }
}));
